/**
 * useConfigurationSelection - Manages configuration and candidate selection state
 *
 * Handles:
 * - Current configuration selection with auto-select logic
 * - Candidate selection with auto-select when configuration changes
 * - Derived state (selectedCandidate, bonds)
 */

import { useDeferredValue, useEffect, useMemo, useState } from 'react';
import { getServerConfigurationId } from '~/hooks/useProjectStore';
import type { Configuration } from '~/types';
import type { Candidate } from '~/types/candidate';
import { useConfigurationIdTransition } from './useConfigurationIdTransition';

interface UseConfigurationSelectionProps {
  configurations: Configuration[] | null;
}

interface UseConfigurationSelectionReturn {
  // Selection state
  selectedConfigurationId: string | null;
  selectedCandidateId: string | null;
  selectedBondId: string | null;

  // Setters
  setSelectedConfigurationId: (id: string | null) => void;
  setSelectedCandidateId: (id: string | null) => void;
  setSelectedBondId: (id: string | null) => void;

  // Derived state
  currentConfiguration: Configuration | null;
  candidates: Candidate[] | null;
  selectedCandidate: Candidate | null;
  bonds: Configuration['bondSeries'];

  // Handlers
  handleConfigurationSelect: (configId: string, targetShapeId?: string) => void;
  handleCandidateSelect: (candidateId: string) => void;
}

export const useConfigurationSelection = ({
  configurations,
}: UseConfigurationSelectionProps): UseConfigurationSelectionReturn => {
  const [selectedConfigurationId, setSelectedConfigurationId] = useState<string | null>(null);
  const [selectedCandidateId, setSelectedCandidateId] = useState<string | null>(null);
  const [selectedBondId, setSelectedBondId] = useState<string | null>(null);

  // Handle ID transitions atomically - replaces the callback-based approach
  useConfigurationIdTransition({ selectedConfigurationId, setSelectedConfigurationId });

  // Auto-select configuration logic
  const currentConfiguration = useMemo(() => {
    if (!configurations || configurations.length === 0) return null;

    // If user has manually selected a configuration, prioritize that
    if (selectedConfigurationId) {
      // First try to find by the selected ID directly
      let selected = configurations.find((c) => c.id === selectedConfigurationId);

      // If not found, check if this is a client ID that was mapped to a server ID
      // This handles the brief moment before useConfigurationIdTransition updates selection
      if (!selected) {
        const serverId = getServerConfigurationId(selectedConfigurationId);
        if (serverId !== selectedConfigurationId) {
          selected = configurations.find((c) => c.id === serverId);
          if (selected) return selected;
        }
      }

      // If found, return it. This ensures manually selected configs are always respected
      if (selected) return selected;

      // If not found yet (might be in the process of being added), fall through to auto-select
    }

    // Auto-select: First try to find a processed configuration with candidates
    const processedConfigs = configurations.filter(
      (config) => config.candidates && config.candidates.length > 0
    );

    if (processedConfigs.length > 0) {
      // Return the most recent processed configuration (newest createdAt)
      return processedConfigs.sort((a, b) => {
        const dateA = new Date(a.createdAt).getTime();
        const dateB = new Date(b.createdAt).getTime();
        return dateB - dateA; // Descending order (newest first)
      })[0];
    }

    // Fallback to most recent configuration (newest createdAt)
    return configurations.sort((a, b) => {
      const dateA = new Date(a.createdAt).getTime();
      const dateB = new Date(b.createdAt).getTime();
      return dateB - dateA; // Descending order (newest first)
    })[0];
  }, [configurations, selectedConfigurationId]);

  // Sync selectedConfigurationId when auto-selection happens
  // Sync when: no selection, OR selected config was deleted (no longer in array)
  useEffect(() => {
    if (!currentConfiguration) return;

    const selectedExists = configurations?.some((c) => c.id === selectedConfigurationId);
    if (!selectedConfigurationId || !selectedExists) {
      setSelectedConfigurationId(currentConfiguration.id);
    }
  }, [currentConfiguration, selectedConfigurationId, configurations]);

  // NOTE: ID transitions (client ID -> server ID) are now handled atomically by
  // useConfigurationIdTransition hook, which reacts to store.lastIdTransition

  // Derived values
  const candidates = currentConfiguration?.candidates || null;
  const failedCandidates = currentConfiguration?.failedCandidates || null;
  const bonds = currentConfiguration?.bondSeries || [];

  // Use deferred value for the candidate lookup so the sidebar selection updates immediately
  // while the heavy data panels (charts, tables) update with lower priority
  const deferredCandidateId = useDeferredValue(selectedCandidateId);

  const selectedCandidate = useMemo(() => {
    if (!deferredCandidateId) return null;
    // First look in viable candidates
    if (candidates) {
      const found = candidates.find((c) => c.id === deferredCandidateId);
      if (found) return found;
    }
    // Then look in failed candidates (for super admin debugging)
    if (failedCandidates) {
      const found = failedCandidates.find((c) => c.id === deferredCandidateId);
      if (found) return found;
    }
    return null;
  }, [deferredCandidateId, candidates, failedCandidates]);

  // Auto-select first candidate when configuration changes or has new candidates
  useEffect(() => {
    if (!currentConfiguration) return;

    const configCandidates = currentConfiguration.candidates || [];
    const configFailedCandidates = currentConfiguration.failedCandidates || [];

    if (configCandidates.length > 0) {
      // If no candidate is selected, or the selected candidate doesn't exist in current config
      // Check both viable AND failed candidates (failed candidates can be selected for debugging)
      const candidateExists = selectedCandidateId
        ? configCandidates.some((c) => c.id === selectedCandidateId) ||
          configFailedCandidates.some((c) => c.id === selectedCandidateId)
        : false;

      if (!candidateExists) {
        // Auto-select the first candidate (top candidate)
        setSelectedCandidateId(configCandidates[0].id);
      }
    } else {
      // Configuration exists but has no candidates yet, clear selection
      if (selectedCandidateId) {
        setSelectedCandidateId(null);
      }
    }
  }, [currentConfiguration, selectedCandidateId]);

  // Handlers
  const handleConfigurationSelect = (configId: string, targetShapeId?: string) => {
    setSelectedConfigurationId(configId);

    // Auto-select candidate from the selected configuration
    const selectedConfig = configurations?.find((c) => c.id === configId);
    if (selectedConfig?.candidates && selectedConfig.candidates.length > 0) {
      if (targetShapeId) {
        // Find candidate with the target shapeId
        const targetCandidate = selectedConfig.candidates.find(
          (c) => c.millageInformation?.millageShape?.shapeId === targetShapeId
        );
        setSelectedCandidateId(targetCandidate?.id || selectedConfig.candidates[0].id);
      } else {
        // Default: select first candidate
        setSelectedCandidateId(selectedConfig.candidates[0].id);
      }
    } else {
      setSelectedCandidateId(null);
    }
  };

  const handleCandidateSelect = (candidateId: string) => {
    setSelectedCandidateId(candidateId);
  };

  return {
    // State
    selectedConfigurationId,
    selectedCandidateId,
    selectedBondId,

    // Setters
    setSelectedConfigurationId,
    setSelectedCandidateId,
    setSelectedBondId,

    // Derived
    currentConfiguration,
    candidates,
    selectedCandidate,
    bonds,

    // Handlers
    handleConfigurationSelect,
    handleCandidateSelect,
  };
};
